import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/store';
import { Compass, Loader2 } from 'lucide-react';

const LoginPage = () => {
  const { user, login, register, error, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to={params.get('next') ?? '/projects'} replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'login') await login(email, password);
      else await register({ email, password, firstName, lastName: lastName || undefined });
      const next = params.get('next') ?? '/projects';
      navigate(next, { replace: true });
    } catch {
      /* error already in store */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-950 p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-archer-500 text-white shadow-lg shadow-archer-500/30">
            <Compass className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Omniapps Archer</h1>
          <p className="text-sm text-slate-400">
            AI-assisted architecture diagramming. Sign in with your Omniapps account.
          </p>
        </div>

        <div className="card">
          <div className="mb-4 flex gap-1 rounded-md bg-slate-800 p-1 text-sm">
            <button
              className={`flex-1 rounded px-3 py-1.5 ${mode === 'login' ? 'bg-slate-900 text-white' : 'text-slate-400'}`}
              onClick={() => setMode('login')}
              type="button"
            >
              Sign in
            </button>
            <button
              className={`flex-1 rounded px-3 py-1.5 ${mode === 'register' ? 'bg-slate-900 text-white' : 'text-slate-400'}`}
              onClick={() => setMode('register')}
              type="button"
            >
              Create account
            </button>
          </div>

          <form className="space-y-3" onSubmit={onSubmit}>
            {mode === 'register' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">First name</label>
                  <input
                    className="input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label">Last name</label>
                  <input
                    className="input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
            )}
            <div>
              <label className="label">Email</label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div>
              <label className="label">Password</label>
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={submitting || loading}
              className="btn-primary w-full"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Archer uses your{' '}
          <Link to="/login" className="underline">
            Omniapps Account
          </Link>{' '}
          for identity and AI credits. By signing in you agree to Omniapps terms.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
